// @ts-strict-ignore
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { PropertyValueType } from '@project/constants/property-value-type';
import { JsonObject, JsonProperty } from '@shared/convert/convert-core';
import { CaeConnection } from '@project/cae/cae-data.service';

export type PropertyValueObjectTypes = 'CaeConnection';

export type PropertyValueObject = {
  type: PropertyValueObjectTypes;
} & CaeConnection;

@JsonObject('IntRangeVO')
export class IntRangeVO {
  @JsonProperty('left', 'Number')
  public left: number = undefined;

  @JsonProperty('right', 'Number')
  public right: number = undefined;

  constructor(left: number = NaN, right: number = NaN) {
    this.left = left;
    this.right = right;
  }

  public equals(intRange: IntRangeVO): boolean {
    if (!intRange) {
      return false;
    }
    return this.left === intRange.left && this.right === intRange.right;
  }

  public clone(): IntRangeVO {
    const clone: IntRangeVO = new IntRangeVO();
    clone.left = this.left;
    clone.right = this.right;
    return clone;
  }

  public contains(value: number): boolean {
    if (isNaN(value)) {
      return false;
    }
    return this.left <= value && value <= this.right;
  }
}

@JsonObject('FloatRangeVO')
export class FloatRangeVO {
  @JsonProperty('left', 'Number')
  public left: number = undefined;

  @JsonProperty('right', 'Number')
  public right: number = undefined;

  constructor(left: number = NaN, right: number = NaN) {
    this.left = left;
    this.right = right;
  }

  public equals(floatRange: FloatRangeVO): boolean {
    if (!floatRange) {
      return false;
    }
    return this.left === floatRange.left && this.right === floatRange.right;
  }

  public clone(): FloatRangeVO {
    const clone: FloatRangeVO = new FloatRangeVO();
    clone.left = this.left;
    clone.right = this.right;
    return clone;
  }
}

@JsonObject('PropertyValueVO')
export class PropertyValueVO {
  @JsonProperty('type', 'String')
  public type: string = undefined;

  @JsonProperty('stringValue', 'String')
  public stringValue: string = undefined;

  @JsonProperty('intValue', 'Number')
  public intValue: number = undefined;

  @JsonProperty('intRange', 'IntRangeVO')
  public intRange: IntRangeVO = undefined;

  @JsonProperty('floatValue', 'Number')
  public floatValue: number = undefined;

  @JsonProperty('floatRange', 'FloatRangeVO')
  public floatRange: FloatRangeVO = undefined;

  @JsonProperty('objectValue', 'Object')
  public objectValue: PropertyValueObject = undefined;

  public static create(type: string = null, stringValue: string = null): PropertyValueVO {
    const propertyValue: PropertyValueVO = new PropertyValueVO();
    propertyValue.type = type;
    propertyValue.stringValue = stringValue;
    return propertyValue;
  }

  public static createObjectValue(objectValue: PropertyValueObject): PropertyValueVO {
    const propertyValue: PropertyValueVO = new PropertyValueVO();
    propertyValue.type = PropertyValueType.OBJECT;
    propertyValue.objectValue = objectValue;
    return propertyValue;
  }

  public equals(propertyValue: PropertyValueVO): boolean {
    if (!propertyValue) {
      return false;
    }
    if (this.type !== propertyValue.type) {
      return false;
    }
    switch (this.type) {
      case PropertyValueType.INT:
        return this.intValue === propertyValue.intValue;
      case PropertyValueType.FLOAT:
        return this.floatValue === propertyValue.floatValue;
      case PropertyValueType.STRING:
        return this.stringValue === propertyValue.stringValue;
      case PropertyValueType.INTRANGE:
        return this.intRange === propertyValue.intRange || (this.intRange && this.intRange.equals(propertyValue.intRange));
      case PropertyValueType.FLOATRANGE:
        return this.floatRange === propertyValue.floatRange || (this.floatRange && this.floatRange.equals(propertyValue.floatRange));
    }
    return false;
  }

  public toString(localeResourceManager?: LocaleResourceManager): string {
    let text = ``;
    switch (this.type) {
      case PropertyValueType.INT: {
        text = localeResourceManager ? localeResourceManager.formatNumber(this.intValue) : this.intValue.toString();
        break;
      }
      case PropertyValueType.FLOAT: {
        text = localeResourceManager ? localeResourceManager.formatNumber(this.floatValue) : this.floatValue.toString();
        break;
      }
      case PropertyValueType.STRING: {
        text = this.stringValue;
        break;
      }
      case PropertyValueType.INTRANGE: {
        const leftValue: string = localeResourceManager ? localeResourceManager.formatNumber(this.intRange.left) : this.intRange.left.toString();
        const rightValue: string = localeResourceManager ? localeResourceManager.formatNumber(this.intRange.right) : this.intRange.right.toString();
        text = `${leftValue} - ${rightValue}`;
        break;
      }
      case PropertyValueType.FLOATRANGE: {
        const leftValue: string = localeResourceManager ? localeResourceManager.formatNumber(this.floatRange.left) : this.floatRange.left.toString();
        const rightValue: string = localeResourceManager
          ? localeResourceManager.formatNumber(this.floatRange.right)
          : this.floatRange.right.toString();
        text = `${leftValue} - ${rightValue}`;
        break;
      }
    }
    return text;
  }

  public clone(): PropertyValueVO {
    const clone: PropertyValueVO = new PropertyValueVO();
    clone.type = this.type;
    clone.stringValue = this.stringValue;
    clone.intValue = this.intValue;
    clone.intRange = this.intRange ? this.intRange.clone() : null;
    clone.floatValue = this.floatValue;
    clone.floatRange = this.floatRange ? this.floatRange.clone() : null;
    return clone;
  }
}

@JsonObject('LocalizedPropertyValueVO')
export class LocalizedPropertyValueVO extends PropertyValueVO {
  @JsonProperty('locale', 'String')
  public locale: string = undefined;
}

@JsonObject('LocalizedPropertyValueWithUnitVO')
export class LocalizedPropertyValueWithUnitVO extends LocalizedPropertyValueVO {
  @JsonProperty('unit', 'String')
  public unit: string = undefined;
}

@JsonObject('PropertyVO')
export class PropertyVO {
  public static readonly CLASS_NAME: string = 'PropertyVO';

  @JsonProperty('type', 'String')
  public type: string = undefined;

  @JsonProperty('name', 'String')
  public name: string = undefined;

  @JsonProperty('values', '[PropertyValueVO]')
  public values: PropertyValueVO[] = undefined;

  @JsonProperty('transientProperty', 'Boolean')
  public transientProperty: boolean = undefined;

  public constructor() {
    this.type = PropertyVO.CLASS_NAME;
  }

  public clone(): PropertyVO {
    const clone: PropertyVO = this.newInstance();
    clone.name = this.name;
    clone.transientProperty = this.transientProperty;

    if (this.values) {
      clone.values = [];
      for (const propertyValue of this.values) {
        clone.values.push(propertyValue.clone());
      }
    }

    return clone;
  }

  protected newInstance(): PropertyVO {
    return new PropertyVO();
  }
}

@JsonObject('ReportGenerationPropertyVO')
export class ReportGenerationPropertyVO extends PropertyVO {
  public static readonly CLASS_NAME: string = 'ReportGenerationPropertyVO';

  @JsonProperty('type', 'String')
  public type: string = undefined;

  @JsonProperty('setType', 'String')
  public setType: string = undefined;

  @JsonProperty('reportId', 'String')
  public reportId: string = undefined;

  public constructor() {
    super();
    this.type = ReportGenerationPropertyVO.CLASS_NAME;
  }

  public clone(): PropertyVO {
    const clone: ReportGenerationPropertyVO = super.clone() as ReportGenerationPropertyVO;
    clone.setType = this.setType;
    clone.reportId = this.reportId;
    return clone;
  }

  protected newInstance(): PropertyVO {
    return new ReportGenerationPropertyVO();
  }
}

@JsonObject('UserSettingPropertyVO')
export class UserSettingPropertyVO extends PropertyVO {
  public static readonly CLASS_NAME: string = 'UserSettingPropertyVO';

  @JsonProperty('type', 'String')
  public type: string = undefined;

  public constructor() {
    super();
    this.type = UserSettingPropertyVO.CLASS_NAME;
  }

  protected newInstance(): PropertyVO {
    return new UserSettingPropertyVO();
  }
}

@JsonObject('LocalizedStringValueVO')
export class LocalizedStringValueVO {
  @JsonProperty('stringValue', 'String')
  public stringValue: string = undefined;

  @JsonProperty('localeKey', 'String')
  public localeKey: string = undefined;
}

@JsonObject('StringValueInLocalesVO')
export class StringValueInLocalesVO {
  @JsonProperty('localizedValues', '[LocalizedStringValueVO]')
  public localizedValues: LocalizedStringValueVO[] = undefined;
}

@JsonObject('PropertyInLocalesVO')
export class PropertyInLocalesVO {
  @JsonProperty('displayName', 'StringValueInLocalesVO')
  public displayName: StringValueInLocalesVO = undefined;

  @JsonProperty('name', 'String')
  public name: string = undefined;

  @JsonProperty('values', 'LocalizedPropertyValueVOsConverter', true)
  public values: LocalizedPropertyValueVO[] = undefined;

  @JsonProperty('order', 'Number')
  public order: number = undefined;
}
